import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import BaseEventCard, { EventCardAppearance } from '../../BaseEventCard';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body } from '~/components/atom/Typography';
import {
  ActivityFields_Event_Contact_Email_Fragment,
  EmailDeliveryStatus,
} from '~/graphql/types';
import { isSuccessEmailDeliveryStatus } from '~/util/constants';
import EmailStatistics from './components/EmailStatistics';
import TEST_ID from './index.testid';
import { EventTimelineContact } from '~/components/template/EventTimelineV2';
import EmailEventMessageBlock from './components/EmailEventMessageBlock';
import { ActivityV2 } from '~/components/template/EventTimelineV2/types';
import TextButton from '~/components/atom/TextButton';
import { bouncedReasonsEmailEvent } from '~/components/page/Contacts/constants';
import StatusContainer from '~/components/organism/StatusContainer';
import type { ComponentLevel } from '~/styles/constants';
import { useTimelineContext } from '../../../context';

export type Props = Omit<ActivityV2, 'highlighted'> & {
  contact: EventTimelineContact;
  appearance?: EventCardAppearance | null;
} & ActivityFields_Event_Contact_Email_Fragment;

const text = {
  subject: 'Onderwerp',
  success: 'De e-mail is verzonden',
  error: 'Er is een fout opgetreden bij het verzenden van de e-mail',
  scheduled: 'Gepland',
  sending: 'Wordt verzonden',
  replyLabel: 'Beantwoorden',
  closeReply: 'Annuleren',
  bouncedLabel:
    'E-mail kon niet worden afgeleverd. Open de contact details voor meer informatie.',
  blockedLabel:
    'E-mail kon niet worden verzonden omdat het e-mailadres van de ontvanger is geblokkeerd.',
};

const getStatusForEmail = (
  status: EmailDeliveryStatus,
  deliveryErrorMessage?: string | null,
): { level: ComponentLevel | null; label: string } => {
  switch (status) {
    case EmailDeliveryStatus.Error:
      return {
        level: 'danger',
        label: `${text.error}${
          deliveryErrorMessage ? `(${deliveryErrorMessage})` : ''
        }`,
      };

    case EmailDeliveryStatus.Synced:
      return { level: 'success', label: text.success };

    case EmailDeliveryStatus.Bounced:
      return {
        level: 'danger',
        label: deliveryErrorMessage
          ? bouncedReasonsEmailEvent[deliveryErrorMessage] ||
            deliveryErrorMessage
          : text.bouncedLabel,
      };

    case EmailDeliveryStatus.Blocked:
      return {
        level: 'danger',
        label: text.blockedLabel,
      };

    case EmailDeliveryStatus.Scheduled:
      return { level: 'loading', label: text.scheduled };

    case EmailDeliveryStatus.Sending:
      return { level: 'loading', label: text.sending };

    // Shall not happen but just in case
    default:
      return { level: null, label: `Onbekende bezorgstatus: ${status}` };
  }
};

const SendEmail: React.FC<Props> = ({
  id,
  header,
  icon,
  sortingDate,
  subject,
  snippet,
  hasMore,
  deliveryStatus,
  tracking,
  from,
  receivedAt,
  deliveryErrorMessage,
  contact,
  ...rest
}) => {
  const { renderEmailReply } = useTimelineContext();
  const [isReplyOpened, setIsReplyOpened] = useState<boolean>(false);

  const status = getStatusForEmail(deliveryStatus, deliveryErrorMessage);
  const hasReplyCapability = !!renderEmailReply;
  const showBottomBar =
    status.level === 'success' && (tracking || hasReplyCapability);

  return (
    <BaseEventCard
      {...rest}
      dataTestId={`${rest.__typename}_Card`}
      header={header}
      icon={icon}
      sortingDate={sortingDate}
      body={
        <JustificationContainer
          margin={['base', 'm', isReplyOpened ? 'l' : null, 'm']}
          direction="column"
        >
          <JustificationContainer gap="base">
            <Body size="base" fontWeight="semiBold" margin={[null]}>
              {subject}
            </Body>
          </JustificationContainer>
          <EmailEventMessageBlock
            id={id}
            emailText={snippet ?? ''}
            hasMore={hasMore}
            canClickOpen={isSuccessEmailDeliveryStatus(deliveryStatus)}
          />

          {status.level !== 'success' && (
            <StatusContainer
              padding={['base']}
              level={status.level}
              label={status.label}
              margin={[null, null, 's', null]}
              dataTestId={TEST_ID.STATUS_CONTAINER}
            />
          )}
        </JustificationContainer>
      }
      bottomBar={
        showBottomBar ? (
          <>
            {hasReplyCapability && isReplyOpened && (
              <JustificationContainer margin={[null, 'm']}>
                {renderEmailReply!({
                  eventId: id,
                  contact,
                  subject: subject ?? '',
                  from,
                  receivedAt,
                  onSuccess: () => setIsReplyOpened(false),
                })}
              </JustificationContainer>
            )}
            <BottomBar
              margin={isReplyOpened ? ['base', null, null, null] : [null]}
              align="center"
              justification="end"
            >
              <JustificationContainer
                align="center"
                padding={['xxs', null, 'xxs', 'm']}
                justification="space-between"
                width="100%"
              >
                <div>{tracking && <EmailStatistics tracking={tracking} />}</div>
                {hasReplyCapability && (
                  <TextButton
                    size="medium"
                    dataTestId={TEST_ID.REPLY_BUTTON}
                    onClick={() => setIsReplyOpened(prev => !prev)}
                    icon={isReplyOpened ? 'chevron-up' : 'reply'}
                    label={isReplyOpened ? text.closeReply : text.replyLabel}
                  />
                )}
              </JustificationContainer>
            </BottomBar>
          </>
        ) : null
      }
    />
  );
};

const BottomBar = styled(JustificationContainer)(
  ({ theme }) => css`
    border-top: 1px solid ${theme.color('tertiary', 'light')};
  `,
);

export default SendEmail;
